import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useProfileQuery, useUpdateProfileMutation } from '@entities/Profile';
import { clearPendingLanguage, useAuthStore, useSignOutMutation } from '@entities/User';
import { trackEvent } from '@shared/analytics';
import { paths } from '@shared/config';
import type { Lang } from '@shared/types';
import { Button, Card, PlainField, SegPills, toast, Toggle } from '@shared/ui';

const REMINDERS_SAVED_MS = 2000;

export function ProfileForm() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const patchUser = useAuthStore((s) => s.patchUser);
  const signOut = useSignOutMutation();
  const updateProfile = useUpdateProfileMutation();
  // Переключатель напоминаний сохраняется сам, своим запросом (B11, 2026-10-20): ушёл, не нажав
  // «Сохранить», — после перезагрузки он снова был включён.
  const saveReminders = useUpdateProfileMutation();
  // Профиль уже в кеше: AppGate не показывает страницу, пока он грузится. Флаг берём из него, а не из
  // стора — стор AppGate дополняет эффектом уже после первого рендера.
  const profile = useProfileQuery().data;

  const [name, setName] = useState(user?.name ?? '');
  const [lang, setLang] = useState<Lang>(user?.lang ?? 'RU');
  const [autoAdvance, setAutoAdvance] = useState(user?.autoAdvance ?? false);
  const [reminders, setReminders] = useState(profile?.is_notification ?? user?.notifications ?? true);
  const [remindersNote, setRemindersNote] = useState<'saved' | 'error' | null>(null);
  // Синхронный guard от двойного клика: updateProfile.isPending (React state)
  // обновляется только на следующий рендер, поэтому два быстрых клика в одном тике
  // успевали отправить два PUT /profile (и два тоста). Ref флипается синхронно.
  const inFlightRef = useRef(false);
  const remindersInFlight = useRef(false);

  useEffect(() => {
    if (remindersNote !== 'saved') return;
    const id = window.setTimeout(() => setRemindersNote(null), REMINDERS_SAVED_MS);
    return () => window.clearTimeout(id);
  }, [remindersNote]);

  const toggleReminders = (next: boolean) => {
    if (remindersInFlight.current) return;
    remindersInFlight.current = true;
    const before = reminders;
    setReminders(next);
    setRemindersNote(null);
    saveReminders.mutate(
      { is_notification: next },
      {
        onSuccess: () => {
          patchUser({ notifications: next });
          trackEvent('reminder_toggle_changed', { enabled: next, place: 'profile' });
          setRemindersNote('saved');
        },
        onError: () => {
          // Не сохранилось — переключатель возвращается, причина — строкой под ним, без тоста.
          setReminders(before);
          setRemindersNote('error');
        },
        onSettled: () => {
          remindersInFlight.current = false;
        },
      },
    );
  };

  const save = () => {
    if (inFlightRef.current || updateProfile.isPending) return;
    inFlightRef.current = true;
    updateProfile.mutate(
      {
        name: name.trim() || undefined,
        language_interface: lang === 'EN' ? 'English' : 'Russian',
        auto_advance: autoAdvance,
      },
      {
        onSuccess: () => {
          // Локально патчим стор для мгновенного UI; смена языка применяется
          // тут же. AppGate всё равно подхватит свежий профиль из кеша.
          patchUser({ name, lang, autoAdvance });
          // Язык выбран явно — отложенный выбор при регистрации (B4) его больше не перебивает.
          clearPendingLanguage();
          void i18n.changeLanguage(lang === 'EN' ? 'en' : 'ru');
          toast({ tone: 'success', message: t('profile.saved') });
        },
        onError: () => {
          toast({ tone: 'error', message: t('profile.saveError') });
        },
        onSettled: () => {
          inFlightRef.current = false;
        },
      },
    );
  };

  const handleSignOut = () => {
    if (signOut.isPending) return;
    signOut.mutate(undefined, {
      onSettled: () => navigate(paths.auth),
    });
  };

  return (
    <>
      <Card padding="md" className="px-5 py-2 nav:px-8">
        <Row k={t('profile.rows.name.k')} h={t('profile.rows.name.h')}>
          <div className="w-full nav:w-80">
            {/* Поле на contenteditable: имя — текстом, поэтому кроме ym-disable-keys нужны
                amp-mask и ym-hide-content, чтобы оно не попало в записи сессий. */}
            <PlainField
              value={name}
              onChange={setName}
              ariaLabel={t('profile.rows.name.k')}
              className="amp-mask ym-hide-content ym-disable-keys block w-full rounded-xl border border-line bg-card px-4 py-3.5 text-sm text-fg outline-none transition-colors focus:border-accent"
            />
          </div>
        </Row>
        <Row k={t('profile.rows.autoAdvance.k')} h={t('profile.rows.autoAdvance.h')}>
          <Toggle
            on={autoAdvance}
            onChange={setAutoAdvance}
            ariaLabel={t('profile.rows.autoAdvance.k')}
          />
        </Row>
        {/* Напоминания (A): сохраняются сразу, без общей кнопки (B11, ТЗ демо 1.10). */}
        <Row k={t('profile.rows.reminders.k')} h={t('profile.rows.reminders.h')}>
          <div className="flex min-h-[24px] items-center gap-3">
            <Toggle
              on={reminders}
              onChange={toggleReminders}
              ariaDisabled={saveReminders.isPending}
              ariaLabel={t('profile.rows.reminders.k')}
            />
            <span aria-live="polite" className="text-[14px] text-muted">
              {remindersNote === 'saved' ? t('profile.rows.reminders.saved') : ''}
            </span>
          </div>
          {remindersNote === 'error' && (
            <div role="alert" className="text-[14px] leading-relaxed text-danger">
              {t('profile.rows.reminders.saveError')}
            </div>
          )}
          {user && !user.emailVerified && (
            <div className="text-[14px] leading-relaxed text-muted">{t('profile.rows.reminders.unverified')}</div>
          )}
        </Row>
        <Row k={t('profile.rows.lang.k')} h={t('profile.rows.lang.h')} last>
          <SegPills
            value={lang}
            onChange={setLang}
            options={[
              { key: 'RU', label: 'Русский' },
              { key: 'EN', label: 'English' },
            ]}
          />
        </Row>
      </Card>
      {/* До 360 px — столбиком: «Сохранить» во всю ширину, под ней «Выйти из аккаунта» — текстовая кнопка по
          левому краю (область 46 px); в ряд подпись рвалась на две строки, а «Сохранить» вытягивалась до 64 px (R2-05). */}
      <div className="mt-8 flex items-center justify-between gap-2 max-[359.98px]:flex-col-reverse max-[359.98px]:items-stretch">
        <Button
          variant="ghost"
          onClick={handleSignOut}
          className="whitespace-nowrap max-[359.98px]:self-start max-[359.98px]:border-transparent max-[359.98px]:px-0 max-[359.98px]:hover:bg-transparent max-[359.98px]:hover:text-accent"
        >
          {t('profile.signOut')}
        </Button>
        <Button onClick={save} disabled={updateProfile.isPending}>
          {updateProfile.isPending ? t('profile.saving') : t('profile.save')}
        </Button>
      </div>
    </>
  );
}

interface RowProps {
  k: string;
  h: string;
  children: ReactNode;
  last?: boolean;
}
function Row({ k, h, children, last }: RowProps) {
  return (
    <div
      className={`grid grid-cols-1 gap-3 py-5 nav:grid-cols-[240px_1fr] nav:gap-8 nav:py-6 ${last ? '' : 'border-b border-line'}`}
    >
      <div>
        <div className="text-[14px] font-medium">{k}</div>
        <div className="mt-1 text-xs leading-relaxed text-muted">{h}</div>
      </div>
      <div className="flex flex-col items-start gap-2">{children}</div>
    </div>
  );
}
